import { CameraControlsImpl } from '@react-three/drei';
import { invalidate } from '@react-three/fiber';
import { Box3, OrthographicCamera, PerspectiveCamera, Sphere, Vector3 } from 'three';

import { CameraProjectionType } from 'app/modules/wallforming/components/scenes/camera-controls/types/cameraProjectionType';
import {
    applyOrthographicFrustum,
    applyPerspectiveFrustum,
    getCameraDistance,
    getOrthographicVisibleHeight,
    getPerspectiveVisibleHeightAtDistance,
    syncCameraPositions
} from 'app/modules/wallforming/components/scenes/camera-controls/utils/cameraUtils';

import type { CameraSliceState } from 'app/modules/wallforming/components/state-management/placement/states/cameraSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

const MIN_DISTANCE = 0.1;
const currentPosition = new Vector3();
const currentTarget = new Vector3();

const top = new Vector3();
const bottom = new Vector3();

const normalA = new Vector3();
const normalB = new Vector3();

const DEFAULT_MAIN_CAMERA_SETTINGS = {
    dollySpeed: 1.5,
    truckSpeed: 2,
    draggingSmoothTime: 0.07,
    azimuthRotateSpeed: 1,
    polarRotateSpeed: 1,
    smoothTime: 0.25,
    dollyToCursor: true
};

export const createCameraSlice: StateCreator<PlacementState, [], [], CameraSliceState> = (set, get) => ({
    mainOrthographicCameraRef: { current: null },
    setMainOrthographicCameraRef: (camera) => {
        const state = get();
        if (state.mainOrthographicCameraRef.current !== camera) {
            state.mainOrthographicCameraRef.current = camera;
        }
    },

    mainPerspectiveCameraRef: { current: null },
    setMainPerspectiveCameraRef: (camera) => {
        const state = get();
        if (state.mainPerspectiveCameraRef.current !== camera) {
            state.mainPerspectiveCameraRef.current = camera;
        }
    },

    mainCameraControlsRef: { current: null },
    setMainCameraControlsRef: (cameraControls) => {
        const state = get();
        if (state.mainCameraControlsRef.current !== cameraControls) {
            state.mainCameraControlsRef.current = cameraControls;
        }
    },

    mainCameraProjectionType: CameraProjectionType.ORTHOGRAPHIC,
    setMainCameraProjectionType: (mode) =>
        set((state) => (state.mainCameraProjectionType === mode ? state : { mainCameraProjectionType: mode })),

    mainCameraSettings: { ...DEFAULT_MAIN_CAMERA_SETTINGS },
    setMainCameraSettings: (settings) =>
        set((state) => ({
            mainCameraSettings: {
                ...state.mainCameraSettings,
                ...settings
            }
        })),
    resetMainCameraSettings: () =>
        set(() => ({
            mainCameraSettings: { ...DEFAULT_MAIN_CAMERA_SETTINGS }
        })),

    isMainCameraLocked: false,
    setIsMainCameraLocked: (isLocked) =>
        set(() => ({
            isMainCameraLocked: isLocked
        })),

    handleLockCameraControls: (setLocked) => {
        const state = get();
        const mainCameraControlsRef = state.mainCameraControlsRef;
        if (mainCameraControlsRef.current) {
            mainCameraControlsRef.current.mouseButtons.middle = setLocked
                ? CameraControlsImpl.ACTION.TRUCK
                : CameraControlsImpl.ACTION.ROTATE;

            requestAnimationFrame(() => {
                state.setIsMainCameraLocked(setLocked);
                invalidate();
            });
        }
    },

    setFrustumOfOrthographicCamera: (cameraControls, canvas, distance) => {
        if (
            cameraControls.current &&
            cameraControls.current.camera instanceof OrthographicCamera &&
            canvas &&
            distance
        ) {
            cameraControls.current.camera.near = -100;
            cameraControls.current.camera.far = distance * 10;
            cameraControls.current.camera.left = canvas.clientWidth / -2;
            cameraControls.current.camera.right = canvas.clientWidth / 2;
            cameraControls.current.camera.top = canvas.clientHeight / 2;
            cameraControls.current.camera.bottom = canvas.clientHeight / -2;

            cameraControls.current.camera.updateProjectionMatrix();
            cameraControls.current.camera.updateMatrixWorld();
        }
    },

    syncMainCameras: (canvas) => {
        const state = get();
        const controls = state.mainCameraControlsRef.current;
        const orthographicCamera = state.mainOrthographicCameraRef.current;
        const perspectiveCamera = state.mainPerspectiveCameraRef.current;
        if (!controls || !orthographicCamera || !perspectiveCamera || !canvas) {
            return;
        }

        const canvasWidth = canvas.clientWidth;
        const canvasHeight = canvas.clientHeight;
        if (canvasWidth <= 0 || canvasHeight <= 0) {
            return;
        }

        const activeCamera = controls.camera;
        const inactiveCamera = activeCamera === orthographicCamera ? perspectiveCamera : orthographicCamera;
        syncCameraPositions(activeCamera, inactiveCamera);

        const safeDistance = Math.max(getCameraDistance(controls, activeCamera), MIN_DISTANCE);
        const aspect = canvasWidth / canvasHeight;
        const visibleHeight =
            activeCamera instanceof OrthographicCamera
                ? getOrthographicVisibleHeight(activeCamera)
                : getPerspectiveVisibleHeightAtDistance(activeCamera, safeDistance);

        if (perspectiveCamera instanceof PerspectiveCamera) {
            applyPerspectiveFrustum(perspectiveCamera, aspect, safeDistance);
        }

        if (orthographicCamera instanceof OrthographicCamera) {
            applyOrthographicFrustum(orthographicCamera, aspect, visibleHeight, safeDistance);
        }
    },

    fitToObject: (boxHelper, cameraControls, resetPosition, updateFrustum, canvas) => {
        boxHelper.geometry.computeBoundingSphere();

        if (boxHelper.geometry.boundingSphere && cameraControls.current) {
            const pos = boxHelper.geometry.boundingSphere.center;
            const distance = boxHelper.geometry.boundingSphere.radius * 10;
            const isMainCamera = cameraControls === get().mainCameraControlsRef;

            if (resetPosition) {
                cameraControls.current.setLookAt(pos.x, pos.y, pos.z + distance, pos.x, pos.y, pos.z, false);
            } else {
                cameraControls.current.setTarget(pos.x, pos.y, pos.z - distance / 2, false);
            }

            const sphere = boxHelper.geometry.boundingSphere;
            sphere.radius *= 1.1;
            cameraControls.current.fitToSphere(sphere, false);

            if (
                !isMainCamera &&
                updateFrustum &&
                cameraControls.current.camera instanceof OrthographicCamera &&
                canvas
            ) {
                get().setFrustumOfOrthographicCamera(cameraControls, canvas, distance);
            }

            if (isMainCamera) {
                get().syncMainCameras(canvas ?? null);
            }

            cameraControls.current.camera.updateProjectionMatrix();
            invalidate();
        }
    },

    fitToPlane(cameraControls, wall, canvas) {
        const geometry = wall.geometry;
        const positionAttribute = geometry.getAttribute('position');

        if (!positionAttribute || positionAttribute.count === 0) {
            return;
        }

        geometry.computeBoundingSphere();
        geometry.computeVertexNormals();

        const normalAttribute = geometry.getAttribute('normal');
        const state = get();

        if (geometry.boundingSphere && normalAttribute && normalAttribute.count > 0 && cameraControls.current) {
            // Use wall normal for camera direction
            const normal = new Vector3().fromBufferAttribute(normalAttribute, 0).normalize();
            const center = geometry.boundingSphere.center;
            const distance = geometry.boundingSphere.radius * 6;
            const isMainCamera = cameraControls === state.mainCameraControlsRef;

            // Place camera along the wall normal (keep enough distance to avoid cutting off areas of the wall)
            const position = center.clone().addScaledVector(normal, distance);
            cameraControls.current.setLookAt(position.x, position.y, position.z, center.x, center.y, center.z, false);
            cameraControls.current.fitToSphere(geometry.boundingSphere, false);

            if (!isMainCamera && cameraControls.current.camera instanceof OrthographicCamera && canvas) {
                state.setFrustumOfOrthographicCamera(cameraControls, canvas, distance);
            }

            if (isMainCamera) {
                state.syncMainCameras(canvas ?? null);
            }

            cameraControls.current.camera.updateProjectionMatrix();
            invalidate();
        }
    },

    setFocusOnWall: (cameraControls, wall, canvas) => {
        const state = get();
        state.fitToPlane(cameraControls, wall, canvas);
        state.handleLockCameraControls(true);
    },

    setFocusOnBuilding: (cameraControlsRef, canvas) => {
        const state = get();
        const buildingHelper = state.buildingHelperRef.current;

        if (!buildingHelper || !cameraControlsRef.current) {
            return;
        }

        buildingHelper.geometry.computeBoundingSphere();
        const boundingSphere = buildingHelper.geometry.boundingSphere;
        if (!boundingSphere) {
            return;
        }

        const scaledSphere = boundingSphere.clone();
        scaledSphere.radius *= 1.1;
        const focusDistance = Math.max(scaledSphere.radius * 2, 1);

        const controls = cameraControlsRef.current;
        const center = boundingSphere.center;
        const isMainCamera = cameraControlsRef === get().mainCameraControlsRef;

        if (!isMainCamera) {
            controls.setTarget(center.x, center.y, center.z, false);
            state.setFrustumOfOrthographicCamera(cameraControlsRef, canvas, focusDistance);
            controls.fitToSphere(scaledSphere, false);
            controls.camera.updateProjectionMatrix();
            invalidate();
            return;
        }

        controls.getPosition(currentPosition);
        controls.getTarget(currentTarget);

        const viewDir = currentPosition.sub(currentTarget);
        if (viewDir.lengthSq() < 0.0001) {
            viewDir.set(0, 0, 1);
        } else {
            viewDir.normalize();
        }

        const nextPosition = center.clone().addScaledVector(viewDir, focusDistance);

        controls.setLookAt(nextPosition.x, nextPosition.y, nextPosition.z, center.x, center.y, center.z, false);
        controls.fitToSphere(scaledSphere, false);

        if (isMainCamera) {
            state.syncMainCameras(canvas ?? null);
        }

        controls.camera.updateProjectionMatrix();
        invalidate();
    },

    setFocusOnCorner: (cameraControls, corner, canvas) => {
        const state = get();
        const controls = cameraControls.current;
        if (!controls) {
            return;
        }

        const { topPoint, bottomPoint, faceA, faceB } = corner;
        top.set(topPoint.x, topPoint.y, topPoint.z);
        bottom.set(bottomPoint.x, bottomPoint.y, bottomPoint.z);

        // Edge midpoint
        const center = top.clone().add(bottom).multiplyScalar(0.5);
        normalA.set(faceA.formFace.normal.x, faceA.formFace.normal.y, faceA.formFace.normal.z);
        normalB.set(faceB.formFace.normal.x, faceB.formFace.normal.y, faceB.formFace.normal.z);

        // Corner bisector
        const viewDir = normalA.clone().add(normalB).normalize();

        // Fallback if normals oppose each other
        if (viewDir.lengthSq() < 0.00001) {
            viewDir.copy(normalA).normalize();
        }

        // Bounding sphere from both face outer wires
        const box = new Box3();

        for (const edge of faceA.formFace.outerWire.edges) {
            const start = new Vector3(edge.startPoint.x, edge.startPoint.y, edge.startPoint.z);
            const end = new Vector3(edge.endPoint.x, edge.endPoint.y, edge.endPoint.z);
            box.expandByPoint(start);
            box.expandByPoint(end);
        }

        for (const edge of faceB.formFace.outerWire.edges) {
            const start = new Vector3(edge.startPoint.x, edge.startPoint.y, edge.startPoint.z);
            const end = new Vector3(edge.endPoint.x, edge.endPoint.y, edge.endPoint.z);
            box.expandByPoint(start);
            box.expandByPoint(end);
        }

        const sphere = new Sphere();
        box.getBoundingSphere(sphere);

        const distance = Math.max(sphere.radius * 10, 1);
        const cameraPosition = center.clone().addScaledVector(viewDir, distance);
        controls.setLookAt(cameraPosition.x, cameraPosition.y, cameraPosition.z + 5, center.x, center.y, center.z, false);

        const isMainCamera = cameraControls === state.mainCameraControlsRef;
        if (!isMainCamera && controls.camera instanceof OrthographicCamera && canvas) {
            state.setFrustumOfOrthographicCamera(cameraControls, canvas, distance);
        }
        if (isMainCamera) {
            state.syncMainCameras(canvas ?? null);
        }

        controls.camera.updateProjectionMatrix();
        invalidate();

        state.handleLockCameraControls(true);
    }
});
